import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AccommodationDetail,
    AccommodationDetailAmenity,
    AssetDetailsResponse,
} from '../../../api/asset/assetApiTypes';
import {
    AMENITIES,
    AMENITY_CATEGORY_ORDER,
    AMENITY_KEYS,
    AMENITY_TYPE,
} from '../../../data/amenities';
import InputField from '../../../components/ui/InputField';
import SelectField from '../../../components/ui/SelectField';
import TextareaField from '../../../components/ui/TextareaField';
import AssetDetailSection from './AssetDetailSection';
import DetailField from './DetailField';
import { spaceOf } from '../../../utils/useCases';

type OutdoorKey =
    | 'none' | 'balcony' | 'loggia' | 'terrace'
    | 'rooftop_terrace' | 'garden' | 'inner_courtyard' | 'other';

const OUTDOOR_KEYS: OutdoorKey[] = [
    'none', 'balcony', 'loggia', 'terrace', 'rooftop_terrace', 'garden', 'inner_courtyard', 'other',
];

interface AccommodationSectionProps {
    /** Omitted in create mode. */
    data?: AssetDetailsResponse;
    amenities?: AccommodationDetailAmenity[];
    useCase?: string;
    mode: 'view' | 'edit' | 'create';
    formData?: AccommodationDetail;
    onAccommodationChange?: (updates: Partial<AccommodationDetail>) => void;
    onAmenitiesChange?: (amenities: AccommodationDetailAmenity[]) => void;
}

const AccommodationSection: React.FC<AccommodationSectionProps> = ({
    data,
    amenities,
    useCase,
    mode,
    formData,
    onAccommodationChange,
    onAmenitiesChange,
}) => {
    const { t, i18n } = useTranslation();
    const detail = data?.accomodationdetail;
    const lang = i18n.language.startsWith('de') ? 'de' : 'en';
    // Event rooms are described by rooms and toilets; stays by beds and baths.
    const isEventRoom = spaceOf(useCase)?.flow === 'event';
    const isEditing = mode === 'edit' || mode === 'create';

    const [amenitySearch, setAmenitySearch] = useState('');

    const facilityDesc = detail?.facilityDescriptions?.[lang]
        ?? detail?.facilityDescription;
    const importantNoteText = detail?.importantNotes?.[lang]
        ?? detail?.importantNote;

    const hasAmenities = amenities && amenities.length > 0;

    const handleNumberChange = (field: keyof AccommodationDetail, value: string) => {
        const parsed = value === '' ? undefined : parseInt(value, 10);
        onAccommodationChange?.({ [field]: Number.isNaN(parsed) ? undefined : parsed });
    };

    // Outdoor area: dropdown with a fallback "Other" option that reveals a
    // free-text field. The stored value is the translated label (or the user's
    // custom text for "Other"). Legacy free-text values that don't match any
    // known option also route through "Other" so they're preserved on edit.
    const outdoorLabels: Record<OutdoorKey, string> = {
        none: t('asset_detail.fields.outdoor_options.none'),
        balcony: t('asset_detail.fields.outdoor_options.balcony'),
        loggia: t('asset_detail.fields.outdoor_options.loggia'),
        terrace: t('asset_detail.fields.outdoor_options.terrace'),
        rooftop_terrace: t('asset_detail.fields.outdoor_options.rooftop_terrace'),
        garden: t('asset_detail.fields.outdoor_options.garden'),
        inner_courtyard: t('asset_detail.fields.outdoor_options.inner_courtyard'),
        other: t('asset_detail.fields.outdoor_options.other'),
    };
    const currentOutdoor = (formData?.outdoor ?? '').trim();
    // Match the stored value against ALL labels including "Sonstiges", so
    // picking Other persists cleanly through a re-render. A non-empty value
    // that doesn't match anything is treated as a custom free-text entry
    // (legacy data or user-typed).
    const matchedOutdoorKey = (Object.keys(outdoorLabels) as OutdoorKey[])
        .find((k) => outdoorLabels[k].toLowerCase() === currentOutdoor.toLowerCase());
    const selectedOutdoorKey: OutdoorKey | '' = matchedOutdoorKey
        ?? (currentOutdoor ? 'other' : '');
    // Populate the text field only when the value is free-text — not when
    // it's exactly the "Sonstiges" placeholder we store after a pick with
    // no typing.
    const outdoorCustomText = (selectedOutdoorKey === 'other' && matchedOutdoorKey !== 'other')
        ? currentOutdoor
        : '';

    const outdoorOptions = OUTDOOR_KEYS.map((k) => outdoorLabels[k]);

    const handleOutdoorSelect = (label: string) => {
        const key = (Object.keys(outdoorLabels) as OutdoorKey[])
            .find((k) => outdoorLabels[k] === label);
        if (!key) return;
        onAccommodationChange?.({ outdoor: outdoorLabels[key] });
    };

    const handleMultilingualChange = (
        field: 'facilityDescriptions' | 'importantNotes',
        lang: string,
        value: string,
    ) => {
        const current = formData?.[field] ?? { de: '', en: '' };
        onAccommodationChange?.({ [field]: { ...current, [lang]: value } });
    };

    // Vocabulary keys are resolved via i18n at render time so the label always
    // follows the viewer's language. Pre-existing freeform entries (key not in
    // the vocabulary) fall back to their stored langValues/value.
    const resolveAmenityLabel = (a: AccommodationDetailAmenity): string => {
        if (AMENITY_KEYS.has(a.key)) return t(`amenities.${a.key}`);
        return a.langValues?.[lang] || (typeof a.value === 'string' ? a.value : String(a.value));
    };

    const handleRemoveAmenity = (key: string) => {
        const updated = (amenities ?? []).filter((a) => a.key !== key);
        onAmenitiesChange?.(updated);
    };

    const selectedKeys = useMemo(
        () => new Set((amenities ?? []).map((a) => a.key)),
        [amenities],
    );

    // Toggle a vocabulary amenity. `value` caches the current-language label for
    // backward compatibility only — rendering always resolves it via i18n.
    const toggleAmenity = (key: string, icon: string) => {
        if (selectedKeys.has(key)) {
            handleRemoveAmenity(key);
            return;
        }
        const newEntry: AccommodationDetailAmenity = {
            type: AMENITY_TYPE,
            key,
            value: t(`amenities.${key}`),
            icon,
        };
        onAmenitiesChange?.([...(amenities ?? []), newEntry]);
    };

    // Group the vocabulary by category, applying the search filter (matches the
    // translated label, case-insensitively). Empty groups are dropped.
    const search = amenitySearch.trim().toLowerCase();
    const groupedAmenities = useMemo(() => {
        return AMENITY_CATEGORY_ORDER
            .map((category) => ({
                category,
                items: AMENITIES.filter((a) => a.category === category
                    && t(`amenities.${a.key}`).toLowerCase().includes(search)),
            }))
            .filter((g) => g.items.length > 0);
    }, [search, t]);

    // Legacy freeform amenities still stored on the asset (not in the vocabulary).
    // Shown so tenants can see and remove them; they can no longer be re-added.
    const customAmenities = (amenities ?? []).filter((a) => !AMENITY_KEYS.has(a.key));

    // `guests` is the only required field here — it drives the availability
    // filter, so an asset without it can never be booked. Marked (and enforced)
    // on create only; existing assets predate the requirement and must stay
    // editable without being forced to backfill it.
    const numberFields: Array<{
        field: keyof AccommodationDetail;
        label: string;
        hideCondition?: boolean;
        required?: boolean;
    }> = [
        { field: 'guests', label: t('asset_detail.fields.guests'), required: true },
        { field: 'rooms', label: isEventRoom ? t('asset_detail.fields.rooms_venue') : t('asset_detail.fields.rooms') },
        { field: 'beds', label: t('asset_detail.fields.beds'), hideCondition: isEventRoom },
        { field: 'baths', label: isEventRoom ? t('asset_detail.fields.toilets') : t('asset_detail.fields.baths') },
        { field: 'kitchen', label: t('asset_detail.fields.kitchen') },
    ];

    const visibleNumberFields = numberFields.filter((nf) => !nf.hideCondition);
    // Keep the grid column count matching the visible field count so every
    // field stretches to fill the section width (venue = 4 cols, apartment = 5).
    const numberColsClass = visibleNumberFields.length === 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-5';

    return (
        <AssetDetailSection title={t('asset_detail.sections.accommodation')}>
            {/* Number fields */}
            <div className={`grid grid-cols-2 ${numberColsClass} gap-4`}>
                {visibleNumberFields.map(({ field, label, required }) => (
                    isEditing ? (
                        <InputField
                            key={field}
                            label={required && mode === 'create' ? `${label} *` : label}
                            type={'number'}
                            min={0}
                            integerOnly={true}
                            value={formData?.[field]?.toString() ?? ''}
                            onChange={(val) => handleNumberChange(field, val)}
                        />
                    ) : (
                        <DetailField
                            key={field}
                            label={label}
                            value={detail?.[field] as number | undefined}
                        />
                    )
                ))}
                {!isEditing && (
                    <DetailField
                        label={t('asset_detail.fields.outdoor')}
                        value={detail?.outdoor}
                    />
                )}
            </div>

            {/* Outdoor area (edit/create) */}
            {isEditing && (
                <div className={'mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                    <SelectField
                        label={t('asset_detail.fields.outdoor')}
                        value={selectedOutdoorKey ? outdoorLabels[selectedOutdoorKey] : ''}
                        onChange={handleOutdoorSelect}
                        options={outdoorOptions}
                    />
                    {selectedOutdoorKey === 'other' && (
                        <InputField
                            label={t('asset_detail.fields.outdoor_other_label')}
                            value={outdoorCustomText}
                            onChange={(val) => onAccommodationChange?.({ outdoor: val })}
                        />
                    )}
                </div>
            )}

            {/* Facility Description (multilingual) */}
            {isEditing ? (
                <div className={'mt-4 flex flex-col gap-3'}>
                    <TextareaField
                        label={`${t('asset_detail.fields.facility_description')} (DE)`}
                        value={formData?.facilityDescriptions?.de ?? ''}
                        onChange={(val) => handleMultilingualChange('facilityDescriptions', 'de', val)}
                        rows={3}
                    />
                    <TextareaField
                        label={`${t('asset_detail.fields.facility_description')} (EN)`}
                        value={formData?.facilityDescriptions?.en ?? ''}
                        onChange={(val) => handleMultilingualChange('facilityDescriptions', 'en', val)}
                        rows={3}
                    />
                </div>
            ) : facilityDesc ? (
                <div className={'mt-4'}>
                    <DetailField
                        label={t('asset_detail.fields.facility_description')}
                        value={facilityDesc}
                    />
                </div>
            ) : null}

            {/* Amenities */}
            {isEditing ? (
                <div className={'mt-4 mb-2'}>
                    <span className={'text-sm text-foreground/60 font-medium'}>
                        {t('asset_detail.sections.amenities')}
                    </span>

                    <div className={'mt-2'}>
                        <InputField
                            label={t('asset_detail.amenity_picker.search_placeholder')}
                            value={amenitySearch}
                            onChange={setAmenitySearch}
                        />
                    </div>

                    {groupedAmenities.length > 0 ? (
                        <div className={'mt-3 flex flex-col gap-4'}>
                            {groupedAmenities.map(({ category, items }) => (
                                <div key={category}>
                                    <span className={'text-xs uppercase tracking-wide text-foreground/50 font-medium'}>
                                        {t(`asset_detail.amenity_picker.groups.${category}`)}
                                    </span>
                                    <div className={'mt-1.5 flex flex-wrap gap-2'}>
                                        {items.map((amenity) => {
                                            const selected = selectedKeys.has(amenity.key);
                                            return (
                                                <button
                                                    key={amenity.key}
                                                    type={'button'}
                                                    aria-pressed={selected}
                                                    onClick={() => toggleAmenity(amenity.key, amenity.icon)}
                                                    className={'text-sm rounded-full px-3 py-1.5 border transition-colors '
                                                        + (selected
                                                            ? 'bg-primary text-text-buttonContrast border-primary'
                                                            : 'border-foreground/20 text-foreground hover:border-primary')}
                                                >
                                                    {t(`amenities.${amenity.key}`)}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className={'mt-3 text-sm text-foreground/50'}>
                            {t('asset_detail.amenity_picker.no_results')}
                        </p>
                    )}

                    {customAmenities.length > 0 && (
                        <div className={'mt-4'}>
                            <span className={'text-xs uppercase tracking-wide text-foreground/50 font-medium'}>
                                {t('asset_detail.amenity_picker.custom_group')}
                            </span>
                            <div className={'mt-1.5 flex flex-wrap gap-2'}>
                                {customAmenities.map((amenity) => (
                                    <span
                                        key={amenity.key}
                                        className={'inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5'
                                            + ' bg-primary text-text-buttonContrast'}
                                    >
                                        {resolveAmenityLabel(amenity)}
                                        <button
                                            type={'button'}
                                            onClick={() => handleRemoveAmenity(amenity.key)}
                                            className={'text-base leading-none hover:opacity-80'}
                                            title={t('asset_detail.actions.remove')}
                                        >
                                            &times;
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : hasAmenities ? (
                <ul className={'mt-3 list-disc list-inside flex flex-col gap-1'}>
                    {(amenities ?? []).map((amenity) => (
                        <li key={amenity.key} className={'text-sm text-foreground'}>
                            {resolveAmenityLabel(amenity)}
                        </li>
                    ))}
                </ul>
            ) : null}

            {/* Important Note (multilingual) */}
            {isEditing ? (
                <div className={'mt-4 flex flex-col gap-3'}>
                    <TextareaField
                        label={`${t('asset_detail.fields.important_note')} (DE)`}
                        value={formData?.importantNotes?.de ?? ''}
                        onChange={(val) => handleMultilingualChange('importantNotes', 'de', val)}
                        rows={2}
                    />
                    <TextareaField
                        label={`${t('asset_detail.fields.important_note')} (EN)`}
                        value={formData?.importantNotes?.en ?? ''}
                        onChange={(val) => handleMultilingualChange('importantNotes', 'en', val)}
                        rows={2}
                    />
                </div>
            ) : importantNoteText ? (
                <div className={'mt-4'}>
                    <DetailField
                        label={t('asset_detail.fields.important_note')}
                        value={importantNoteText}
                    />
                </div>
            ) : null}
        </AssetDetailSection>
    );
};

export default AccommodationSection;
